import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Globe, Gamepad2, Users, Sparkles, Scroll, Mic2, Heart, Clapperboard, MessageCircle, Shield, Ticket, Zap, Gift, Smartphone, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import AppCarousel from '../components/AppCarousel';
import PromoTicker from '../components/PromoTicker';
import { getNextEvent } from '../data/upcomingEvents';

// Kingvention Early Bird deadline — 23:59 UK BST on 27 July 2026.
// While this window is live the hero countdown targets the promo deadline
// (max FOMO). After it passes we fall back to the next real event automatically.
const EARLY_BIRD_END = new Date('2026-07-27T22:59:59.999Z');

const Home = () => {
  // Members app subscription link
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Dynamic countdown — targets the soonest future event from the Events page.
  // When the current target passes, the next future event auto-takes over.
  const promoActive = () => Date.now() < EARLY_BIRD_END.getTime();
  const initialTarget = promoActive()
    ? {
        id: 'kingvention-early-bird-deadline',
        organiser: 'PopCrown',
        title: 'Early Bird Bundle Closes',
        city: 'London',
        startDate: EARLY_BIRD_END.toISOString(),
        displayDate: 'Ends 23:59 · Mon 27 July 2026',
        isPromo: true,
      }
    : getNextEvent();

  const [nextEvent, setNextEvent] = useState(initialTarget);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const current = now < EARLY_BIRD_END.getTime()
        ? {
            id: 'kingvention-early-bird-deadline',
            organiser: 'PopCrown',
            title: 'Early Bird Bundle Closes',
            city: 'London',
            startDate: EARLY_BIRD_END.toISOString(),
            displayDate: 'Ends 23:59 · Mon 27 July 2026',
            isPromo: true,
          }
        : getNextEvent(now);
      // If the target rolled forward (or disappeared), sync state.
      setNextEvent((prev) => (prev?.id === current?.id ? prev : current));
      if (!current) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      const distance = new Date(current.startDate).getTime() - now;
      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // Badge copy adapts to whichever event is next.
  const badgeText = nextEvent
    ? nextEvent.isPromo
      ? 'EARLY BIRD ENDS SOON — POPCROWN EXCLUSIVE BUNDLE'
      : `NEXT EVENT — ${nextEvent.organiser.toUpperCase()} ${nextEvent.title.toUpperCase()} — ${nextEvent.city.toUpperCase()}`
    : 'MICHAEL MANIA IS HERE — JOIN 130+ COUNTRIES OF MJ FANS';

  const valueProps = [
    {
      Icon: Clapperboard,
      title: 'The Movie Event of 2026 — Now On Amazon Prime',
      description: '\u201cMichael\u201d shattered the music-biopic record with a $217.4M global opening and has officially crossed $1 billion worldwide \u2014 the highest-grossing music biopic of all time and the highest-grossing Lionsgate film ever. Soundtrack hit #1 in the UK and New Zealand. Michael\u2019s autobiography \u201cMoonwalk\u201d back to #1 on Amazon. Sequel in active development at Lionsgate. Available to rent or buy on Amazon Prime Video, Apple TV, YouTube & more.'
    },
    {
      Icon: Globe,
      title: '130+ Countries Connected',
      description: 'Fans from every corner of the globe. One conversation. One community. Are you in?'
    },
    {
      Icon: Gamepad2,
      title: 'Daily Quizzes & Games',
      description: 'You think you know Michael? Prove it. Daily challenges that separate the real fans from everyone else.'
    },
    {
      Icon: MessageCircle,
      title: 'The Conversation is Happening',
      description: 'Real fans. Real discussions. Real connections. The community is growing fast - don\'t miss out.'
    }
  ];

  return (
    <div className="min-h-screen">
      <PromoTicker />
      {/* SEO Meta Tags - Homepage */}
      <Helmet>
        <title>PopCrown — The Ultimate Michael Jackson Fan Community | MJ App, Quizzes & Leaderboard</title>
        <meta name="description" content="PopCrown is the world's only Michael Jackson fan app. Daily MJ quizzes, Legal Vault, global leaderboard. 130+ countries. From £1.99/mo." />
        <meta name="keywords" content="Michael Jackson fan community, MJ fan app, Michael Jackson quiz, King of Pop, MJ leaderboard, Michael Jackson biopic 2026, Michael Jackson Legal Vault, PopCrown, Michael Jackson fans, MJ trivia, Michael Jackson news, Michael Jackson community, Michael Jackson social network, MJ fan club, Michael Jackson app, Michael Jackson game, Michael Jackson tribute, Thriller, Beat It, Billie Jean, Bad, Smooth Criminal, Dangerous, HIStory, Off the Wall, Moonwalker, Neverland, Jackson 5, Janet Jackson" />
        <link rel="canonical" href="https://popcrownofficial.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://popcrownofficial.com/" />
        <meta property="og:title" content="PopCrown — The Ultimate Michael Jackson Fan Community" />
        <meta property="og:description" content="Daily MJ quizzes, global leaderboard, Legal Vault, and exclusive content. Join fans from 130+ countries celebrating the King of Pop." />
        <meta property="og:image" content="https://popcrownofficial.com/hero_mural_optimized.jpg" />
        <meta property="og:site_name" content="PopCrown Official" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PopCrown — The Ultimate Michael Jackson Fan Community" />
        <meta name="twitter:description" content="Daily MJ quizzes, global leaderboard, Legal Vault. Join 130+ countries celebrating the King of Pop." />
        <meta name="twitter:image" content="https://popcrownofficial.com/hero_mural_optimized.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "PopCrown — The Ultimate Michael Jackson Fan Community",
          "description": "Join PopCrown, the world's biggest Michael Jackson fan community. Daily MJ quizzes, global leaderboard, Legal Vault, community feed, and exclusive content.",
          "url": "https://popcrownofficial.com/",
          "isPartOf": { "@type": "WebSite", "name": "PopCrown Official", "url": "https://popcrownofficial.com" },
          "about": {
            "@type": "Person",
            "name": "Michael Jackson",
            "alternateName": ["MJ", "King of Pop", "Michael Joseph Jackson"],
            "description": "American singer, songwriter, dancer, and philanthropist. Known as the King of Pop.",
            "birthDate": "1958-08-29",
            "birthPlace": "Gary, Indiana, USA",
            "genre": ["Pop", "R&B", "Rock", "Soul", "Funk", "Disco", "New Jack Swing"],
            "award": ["Grammy Award", "World Music Award", "American Music Award", "MTV Video Music Award", "Rock and Roll Hall of Fame"],
            "sameAs": [
              "https://en.wikipedia.org/wiki/Michael_Jackson",
              "https://www.wikidata.org/wiki/Q2831"
            ]
          },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "h2", "[data-testid='hero-section']"]
          },
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "PopCrown",
            "applicationCategory": "SocialNetworkingApplication",
            "operatingSystem": "iOS, Android, Web",
            "offers": [
              { "@type": "Offer", "name": "PopCrown App", "price": "0", "priceCurrency": "GBP", "description": "Free — daily MJ quizzes, Legal Vault, In His Words, global leaderboard." },
              { "@type": "Offer", "name": "PopCrown Community", "price": "1.99", "priceCurrency": "GBP", "billingIncrement": "P1M" }
            ]
          }
        })}</script>
      </Helmet>

      {/* Hero Section - MOVIE EVENT FOMO */}
      <section className="relative min-h-[100vh] md:min-h-[95vh] flex items-center justify-center pt-24 md:pt-36 pb-8 md:pb-16 overflow-hidden" data-testid="hero-section">
        {/* Background Image - Zoom focused on Michael's eyes */}
        <picture className="absolute inset-0 w-full h-full">
          <source srcSet="/hero_mural_optimized.webp" type="image/webp" />
          <img 
            src="/hero_mural_optimized.jpg" 
            alt="Michael Jackson street art mural painting - iconic King of Pop portrait"
            className="w-full h-full object-cover"
            style={{ 
              objectPosition: 'center 38%',
              transform: 'scale(2.2)',
              transformOrigin: 'center 42%'
            }}
            width="1920"
            height="1122"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        
        {/* Overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(225, 6, 0, 0.2) 100%)'
        }} />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          {/* Top badge + dynamic countdown clock (next event) */}
          <div className="mb-3">
            <div className="inline-block px-4 py-1.5 rounded-full mb-2" style={{
              background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
            }}>
              <span className="text-white font-bold text-xs" data-testid="countdown-label">{badgeText}</span>
            </div>
            {nextEvent && (
              <>
                <div className="w-full flex justify-center">
                  <div className="p-3 md:p-4 rounded-xl max-w-[92vw]" style={{
                    background: 'rgba(0, 0, 0, 0.75)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid #E10600',
                    boxShadow: '0 0 40px rgba(225, 6, 0, 0.3)'
                  }}>
                    <div className="flex gap-1.5 sm:gap-3 md:gap-4 justify-center items-center" data-testid="countdown-clock">
                      <div className="text-center min-w-[40px]">
                        <div className="text-xl sm:text-2xl md:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: 'monospace' }}>
                          {String(countdown.days).padStart(2, '0')}
                        </div>
                        <div className="text-[#E10600] text-[9px] sm:text-[10px] md:text-xs font-semibold">DAYS</div>
                      </div>
                      <div className="text-xl sm:text-2xl md:text-4xl font-bold text-[#E10600]">:</div>
                      <div className="text-center min-w-[40px]">
                        <div className="text-xl sm:text-2xl md:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: 'monospace' }}>
                          {String(countdown.hours).padStart(2, '0')}
                        </div>
                        <div className="text-[#E10600] text-[9px] sm:text-[10px] md:text-xs font-semibold">HRS</div>
                      </div>
                      <div className="text-xl sm:text-2xl md:text-4xl font-bold text-[#E10600]">:</div>
                      <div className="text-center min-w-[40px]">
                        <div className="text-xl sm:text-2xl md:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: 'monospace' }}>
                          {String(countdown.minutes).padStart(2, '0')}
                        </div>
                        <div className="text-[#E10600] text-[9px] sm:text-[10px] md:text-xs font-semibold">MIN</div>
                      </div>
                      <div className="text-xl sm:text-2xl md:text-4xl font-bold text-[#E10600]">:</div>
                      <div className="text-center min-w-[40px]">
                        <div className="text-xl sm:text-2xl md:text-4xl font-bold text-white tabular-nums" style={{ fontFamily: 'monospace' }}>
                          {String(countdown.seconds).padStart(2, '0')}
                        </div>
                        <div className="text-[#E10600] text-[9px] sm:text-[10px] md:text-xs font-semibold">SEC</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full flex justify-center mt-2">
                  <Link
                    to={nextEvent.isPromo ? '/join' : '/events'}
                    className="px-3 py-1 rounded-full text-white text-xs md:text-sm font-semibold hover:text-[#E10600] transition-colors"
                    style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
                    data-testid="countdown-event-link"
                  >
                    {nextEvent.isPromo
                      ? 'Get My Member Code — Join Free →'
                      : `${nextEvent.displayDate} · ${nextEvent.city} →`}
                  </Link>
                </div>
              </>
            )}
          </div>
          
          {/* Crown Logo */}
          <div className="flex items-center justify-center mb-2">
            <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 object-contain" />
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-2">
            <span className="thriller-text">PopCrown</span>
            <span className="sr-only"> — Michael Jackson Fan Community</span>
          </h1>
          
          {/* Main FOMO headline */}
          <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
            The Michael Jackson Fan App.
          </h2>
          <h3 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            <span className="thriller-text">FREE</span> to Download. <span className="thriller-text">FREE</span> Forever.
          </h3>
          <p className="text-base md:text-lg text-gray-200 max-w-2xl mx-auto mb-4">
            Daily MJ quizzes, the Legal Vault, Michael in his own words, leaderboard — <strong className="text-white">all yours, no card needed.</strong>{' '}
            <br className="hidden md:block" />
            Want to <span className="text-[#E10600] font-bold">join the conversation</span>, grab <span className="text-[#E10600] font-bold">early event tickets</span> &amp; <span className="text-[#E10600] font-bold">discounts</span>? £1.99/month keeps our community safe &amp; moderated.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-3">
            <a href="/app#install" className="btn-gold min-h-[44px] flex items-center justify-center text-base px-6" aria-label="Install PopCrown free on your phone" data-testid="hero-download-cta">
              <span className="inline-flex items-center gap-2"><Smartphone className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> INSTALL FREE — IT'S APP-LIKE</span>
            </a>
            <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-outline-gold min-h-[44px] flex items-center justify-center px-6" aria-label="Join PopCrown Community" data-testid="hero-community-cta">
              JOIN THE COMMUNITY — £1.99/mo
            </a>
          </div>
          
          {/* Disclaimer */}
          <p className="text-[10px] text-gray-400">
            (Unofficial fan community — not affiliated with any estates or rights holders)
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20" style={{
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 50%, #FFF5F5 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              <span className="text-black">Why Fans Are </span>
              <span className="thriller-text">Joining</span>
            </h2>
            <div className="mx-auto mb-8 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
            
            <div className="text-lg md:text-xl text-gray-700 leading-relaxed mb-10 px-4">
              <p className="mb-6">
                The Michael Jackson biopic is here. This is the moment Michael Jackson fans have been waiting for — and PopCrown is where the celebration is happening.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Inside PopCrown, you'll find everything a Michael Jackson fan needs:
              </p>
              <ul className="text-left max-w-2xl mx-auto space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Gamepad2 className="w-5 h-5 mt-1 shrink-0 text-[#E10600]" aria-hidden="true" />
                  <span><strong>Think you know Michael Jackson?</strong> — Prove it. Daily quizzes on Michael Jackson's music, tours, and legacy. Climb the global leaderboard or get humbled.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Scroll className="w-5 h-5 mt-1 shrink-0 text-[#E10600]" aria-hidden="true" />
                  <span><strong>The Michael Jackson Legal Vault</strong> — court documents, case timelines, and the facts the media won't tell you</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mic2 className="w-5 h-5 mt-1 shrink-0 text-[#E10600]" aria-hidden="true" />
                  <span><strong>In Michael's Own Words</strong> — Michael Jackson's quotes and interviews, fully searchable</span>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 mt-1 shrink-0 text-[#E10600]" aria-hidden="true" />
                  <span><strong>A global Michael Jackson community</strong> — connect with fans of the King of Pop in 130+ countries</span>
                </li>
              </ul>
            </div>

            <div className="relative inline-block">
              <div className="absolute inset-0 blur-2xl opacity-30" style={{
                background: 'radial-gradient(circle, rgba(225, 6, 0,0.8) 0%, transparent 70%)'
              }}></div>
              <blockquote className="relative text-4xl md:text-6xl text-gray-900 px-8 py-8" style={{
                fontFamily: "'Anton', 'Oswald', 'Impact', sans-serif",
                background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.05) 0%, rgba(220, 20, 60, 0.05) 100%)',
                borderRadius: '0.5rem',
                borderLeft: '4px solid #E10600',
                lineHeight: '1.05',
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                fontWeight: 400
              }}>
                <span className="text-[#DC143C]">&ldquo;</span>The world is getting ready. Are you?<span className="text-[#DC143C]">&rdquo;</span>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section className="py-20" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
      }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              See What Michael Jackson Fans Get Inside <span className="thriller-text">PopCrown</span>
            </h2>
            <p className="text-gray-600 text-lg">
              The only Michael Jackson fan app built for real fans who want community, not chaos.
            </p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <AppCarousel />
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 relative" style={{
        background: 'linear-gradient(180deg, #F8F8F8 0%, #FFFFFF 50%, #F8F8F8 100%)'
      }}>
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, #E10600 0%, transparent 50%), radial-gradient(circle at 75% 75%, #DC143C 0%, transparent 50%)'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-black">What We </span>
              <span className="thriller-text">Stand For</span>
            </h2>
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">
              A Michael Jackson fan community built on belonging, safety, and truth — not tabloid drama
            </p>
            <div className="mx-auto mt-4 h-1 w-24 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {valueProps.map((prop, index) => {
              const IconEl = prop.Icon;
              return (
                <div 
                  key={index}
                  className="p-8 rounded-2xl text-center fade-in-up transition-all hover:transform hover:scale-105 hover:shadow-xl group bg-white"
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    border: '1px solid rgba(225, 6, 0, 0.12)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.05)'
                  }}
                >
                  <div className="mb-4 flex justify-center transition-transform group-hover:scale-110">
                    <IconEl className="w-10 h-10 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-3">
                    {prop.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {prop.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 relative shimmer-bg" style={{
        background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 50%, #FFF5F5 100%)'
      }}>
        {/* Premium Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0 L60 40 L100 50 L60 60 L50 100 L40 60 L0 50 L40 40 Z' fill='%23DC143C' fill-opacity='0.3'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-8 py-3 rounded-full mb-6" style={{
              background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
              boxShadow: '0 0 30px rgba(220, 20, 60, 0.5)'
            }}>
              <span className="inline-flex items-center gap-2 text-white font-bold text-lg"><Smartphone className="w-5 h-5" strokeWidth={2} aria-hidden="true" /> INSTALL FREE — PWA AVAILABLE NOW · APP STORE COMING SOON</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-black">The App is </span>
              <span className="thriller-text">FREE</span>
            </h2>
            <p className="text-gray-700 text-lg md:text-xl max-w-2xl mx-auto">
              Want to <strong>join the conversation</strong>? That&apos;s the <strong className="text-[#E10600]">PopCrown Community</strong> — and it&apos;s less than a Costa.
            </p>
            <div className="mx-auto mt-4 h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
            {/* FREE APP — clean, no faded image */}
            <div className="p-8 pt-12 relative rounded-xl flex flex-col" style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
              border: '2px solid #E0E0E0',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)'
            }} data-testid="free-app-card">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full font-bold text-sm whitespace-nowrap" style={{
                background: '#000000',
                color: '#FFFFFF',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)'
              }}>
              <span className="inline-flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" /> NO CARD NEEDED
              </span>
              </div>
              <div className="flex flex-col flex-grow">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-black mb-2">The App</h3>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-6xl md:text-7xl font-extrabold thriller-text" style={{ lineHeight: 1 }}>FREE</span>
                  </div>
                  <div className="text-gray-600 text-sm mt-2">Forever · iPhone &amp; Android</div>
                </div>
                <ul className="space-y-3 mb-8 flex-grow">
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span><strong>Daily MJ quizzes &amp; games</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span><strong>The Legal Vault</strong> — Documents &amp; timelines</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span><strong>In His Words</strong> — Michael&apos;s own quotes, searchable</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span><strong>Global leaderboard</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span>130+ languages supported</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-800">
                    <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                    <span>Zero ads. Zero spam. Zero paywall on the good stuff.</span>
                  </li>
                </ul>
                <div className="mt-auto">
                  <a href="/app#install" className="btn-gold w-full text-center min-h-[44px] flex items-center justify-center" data-testid="free-download-btn">
                    Install Free on Your Phone →
                  </a>
                  <p className="text-center text-xs text-gray-600 mt-2">iPhone &amp; Android · No card needed · Forever</p>
                </div>
              </div>
            </div>

            {/* CROWN COMMUNITY — paid */}
            <div className="p-8 relative rounded-xl flex flex-col" style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFE4E0 50%, #FFFFFF 100%)',
              border: '3px solid #E10600',
              boxShadow: '0 20px 60px rgba(225, 6, 0, 0.4), 0 10px 30px rgba(220, 20, 60, 0.3), 0 0 0 1px rgba(225, 6, 0, 0.2)'
            }} data-testid="crown-community-card">
              {/* Shimmer overlay */}
              <div className="absolute inset-0 opacity-10 pointer-events-none rounded-xl" style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(225, 6, 0, 0.5) 50%, transparent 70%)',
                backgroundSize: '200% 200%',
                animation: 'shimmer 10s infinite'
              }}></div>
              
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full font-bold text-sm whitespace-nowrap" style={{
                background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                color: '#FFFFFF',
                boxShadow: '0 4px 15px rgba(225, 6, 0, 0.6)',
                border: '2px solid #DC143C'
              }}>
                <span className="inline-flex items-center gap-2"><Crown className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> THE INNER CIRCLE <Crown className="w-4 h-4" strokeWidth={2} aria-hidden="true" /></span>
              </div>
              <div className="text-center mb-6 mt-4">
                <h3 className="text-2xl font-bold text-black mb-2">PopCrown Community</h3>
                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className="text-6xl md:text-7xl font-bold text-[#DC143C]" style={{ lineHeight: 1 }}>£1.99</span>
                  <span className="text-gray-500 text-base ml-1">/month</span>
                </div>
                <div className="text-gray-600 text-sm mt-2">Less than 7p a day · Cancel anytime</div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-start gap-2 text-gray-800">
                  <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                  <span><strong>Everything in the free app</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-800 bg-[#FFE4E0] p-2 rounded-lg -mx-2">
                  <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                  <span><strong>Join the conversation</strong> — community feed with fans in 130+ countries</span>
                </li>
                <li className="flex items-start gap-2 text-gray-800 bg-[#FFE4E0] p-2 rounded-lg -mx-2">
                  <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                  <span><strong>Early access to event tickets</strong></span>
                </li>
                <li className="flex items-start gap-2 text-gray-800 bg-[#FFE4E0] p-2 rounded-lg -mx-2">
                  <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                  <span><strong>Real discount codes</strong> — merch, tickets, partner perks</span>
                </li>
                <li className="flex items-start gap-2 text-gray-800">
                  <Check className="w-4 h-4 text-[#E10600] shrink-0 mt-1" strokeWidth={2.5} aria-hidden="true" />
                  <span>Fan Points &amp; exclusive members-only badges</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700 text-sm italic mt-3 pt-3 border-t border-[#FFD0CB]">
                  <Shield className="w-5 h-5 text-[#E10600] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                  <span>Your fee keeps the community safe, moderated &amp; legally compliant.</span>
                </li>
              </ul>
              <div className="mt-auto" data-testid="crown-stripe-btn">
                <stripe-buy-button
                  buy-button-id="buy_btn_1TlVm3EGgMgYLhYhjcfnIfe0"
                  publishable-key="pk_live_51Sgn32EGgMgYLhYhWQ8WMBIiVvNSt7JcpG4U9rulRMmwgfE18K1sREeSLn0zrXcgFi45SOnmD7FlIxT4n9LTkASR00UzJhyl7m"
                >
                </stripe-buy-button>
                <p className="text-center text-xs text-gray-600 mt-2">£1.99 per month · Cancel anytime</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIP Community Section */}
      <section className="py-20 relative" style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF0F0 50%, #FFFFFF 100%)'
      }}>
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, #E10600 0%, transparent 50%), radial-gradient(circle at 70% 50%, #DC143C 0%, transparent 50%)'
        }}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="blog-card p-8 md:p-12 text-center relative overflow-hidden" style={{
              border: '2px solid',
              borderImage: 'linear-gradient(135deg, #DC143C, #E10600, #FFFFFF, #E10600, #DC143C) 1',
              boxShadow: '0 20px 40px rgba(220, 20, 60, 0.2), 0 10px 20px rgba(225, 6, 0, 0.1)'
            }}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Join the VIP list for <span className="text-[#E10600]">drops, discounts, and early access</span>
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Follow us and subscribe to get the good stuff first.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <Ticket className="w-9 h-9 mx-auto mb-2 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="text-white font-semibold mb-2">VIP Discount Codes</h3>
                  <p className="text-gray-400 text-sm">Exclusive discounts on memberships, event tickets, and merch.</p>
                </div>
                <div className="text-center">
                  <Zap className="w-9 h-9 mx-auto mb-2 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="text-white font-semibold mb-2">Early Access</h3>
                  <p className="text-gray-400 text-sm">Be first to know about new features, events, and releases.</p>
                </div>
                <div className="text-center">
                  <Gift className="w-9 h-9 mx-auto mb-2 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="text-white font-semibold mb-2">Exclusive Content</h3>
                  <p className="text-gray-400 text-sm">Fan highlights, behind the scenes updates, and community surprises.</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="https://instagram.com/popcrownsocial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-outline-gold"
                >
                  Follow on Instagram
                </a>
                <a 
                  href="https://facebook.com/PopCrownSocial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-outline-gold"
                >
                  Like on Facebook
                </a>
                <a 
                  href="https://tiktok.com/@popcrownsocial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-outline-gold"
                >
                  Follow on TikTok
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is PopCrown - SEO Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="text-black">The World's Only </span>
            <span className="thriller-text">Michael Jackson Fan App</span>
          </h2>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p className="text-lg leading-relaxed">
              PopCrown is the world's first and only dedicated Michael Jackson fan community app. Whether you've loved the King of Pop since the Thriller era or discovered Michael Jackson through the 2026 biopic starring Jaafar Jackson, PopCrown is where Michael Jackson fans belong.
            </p>
            <p className="text-lg leading-relaxed">
              Unlike social media pages that come and go, PopCrown is a permanent home for Michael Jackson fans — a place built specifically around Michael Jackson's music, legacy, and the community that keeps it alive. From daily Michael Jackson trivia quizzes to the world's most comprehensive Legal Vault of court documents proving Michael Jackson's innocence, everything here is designed for fans who care about the truth.
            </p>
            <p className="text-lg leading-relaxed">
              PopCrown features include a global Michael Jackson quiz leaderboard (with fans competing from 130+ countries), a searchable archive of Michael Jackson's own words and interviews, Michael Jackson tour locations around the world, curated Michael Jackson fan event listings, and a safe community feed where Michael Jackson fans can connect without trolls or tabloid nonsense.
            </p>
          </div>

          {/* FAQ Section - Rich Snippets for Google */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center mb-8 text-black">Frequently Asked Questions</h3>
            <div className="space-y-6">
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">What is PopCrown?</summary>
                <p className="mt-3 text-gray-700">PopCrown is the world's only dedicated Michael Jackson fan community app. It brings together Michael Jackson fans from over 130 countries with daily quizzes, the Legal Vault, community features, and exclusive content celebrating the King of Pop's legacy.</p>
              </details>
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">Is PopCrown the only Michael Jackson fan app?</summary>
                <p className="mt-3 text-gray-700">Yes. PopCrown is the world's first and only dedicated Michael Jackson fan app. While there are social media fan pages, PopCrown is the only purpose-built mobile app designed exclusively for Michael Jackson fans, with features like the Legal Vault, daily MJ quizzes, and a global leaderboard.</p>
              </details>
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">What is the Michael Jackson Legal Vault?</summary>
                <p className="mt-3 text-gray-700">The Legal Vault is PopCrown's exclusive archive of court documents, FBI investigation records, and legal timelines related to Michael Jackson's cases. It contains over 70 documents spanning 10+ years of investigations — all of which cleared Michael Jackson completely. 14 allegations, 14 clearances.</p>
              </details>
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">How much does PopCrown cost?</summary>
                <p className="mt-3 text-gray-700">The PopCrown app is <strong>FREE</strong> — install it from your iPhone or Android browser today (App Store and Google Play listings coming soon). You get daily MJ quizzes, the Legal Vault, In His Words, the global leaderboard and more, no card needed. If you&apos;d like to join the conversation, get early access to event tickets and exclusive discounts, the PopCrown Community is £1.99/month. Your membership fee keeps the community safe, moderated &amp; legally compliant. Cancel anytime.</p>
              </details>
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">Does PopCrown list Michael Jackson fan events?</summary>
                <p className="mt-3 text-gray-700">Yes. PopCrown curates Michael Jackson fan events, tribute concerts, immersive experiences and community meetups worldwide. Visit our Events page to see what's coming up — including featured community partners like The MJ Experience in London (Sip &amp; Paint nights, Rhinestone Glove workshops and more).</p>
              </details>
              <details className="border-b border-gray-200 pb-4">
                <summary className="font-semibold text-lg cursor-pointer text-black hover:text-[#DC143C]">Can I join PopCrown from any country?</summary>
                <p className="mt-3 text-gray-700">Absolutely. PopCrown has Michael Jackson fans from over 130 countries. The app supports 130+ languages and the community is truly global. Whether you're a Michael Jackson fan in the UK, USA, Brazil, Japan, or anywhere else — you're welcome here.</p>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, #E10600 0%, transparent 50%), radial-gradient(circle at 80% 80%, #FF0000 0%, transparent 50%)'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-block px-6 py-2 rounded-full mb-6" style={{
            background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
          }}>
            <span className="inline-flex items-center gap-2 text-white font-bold"><Smartphone className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> INSTALL FREE NOW · APP STORE COMING SOON</span>
          </div>
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-16 h-16 mx-auto mb-6 crown-pulse object-contain" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Install <span className="thriller-text">FREE</span>. Join the Conversation.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            130+ countries. The Michael Jackson fan app — free forever. Want to <strong className="text-white">join the conversation</strong>, get <strong className="text-white">early event tickets</strong> &amp; <strong className="text-white">discounts</strong>? £1.99/month.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/app#install"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="final-download-cta"
            >
              <span className="inline-flex items-center gap-2"><Smartphone className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> Install Free on Your Phone</span>
            </a>
            <a
              href={subscriptionJoinLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold inline-flex items-center gap-2"
              data-testid="final-community-cta"
            >
              <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain" />
              Join PopCrown Community — £1.99/mo
            </a>
          </div>
          <p className="text-xs text-gray-300 mt-4">Free forever · PopCrown Community cancel anytime · 130+ languages</p>
        </div>
      </section>

      {/* Photo Credits - SEO and Attribution */}
      <section className="py-4 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <p className="text-xs text-gray-400 text-center">
            Hero image: "Michael Jackson Street Art Mural in Montmartre, Paris" by{' '}
            <a 
              href="https://www.pexels.com/@valeska-huyskens-414046059/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-[#E10600] transition-colors underline"
            >
              Valeska Huyskens
            </a>
            {' '}via{' '}
            <a 
              href="https://www.pexels.com/photo/colorful-street-art-mural-in-montmartre-paris-32591593/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-[#E10600] transition-colors underline"
            >
              Pexels
            </a>
            {' '}(Free to use)
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
